import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { motion } from 'framer-motion';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { useTechnologyEmbedded } from '../hooks/useTechnologyEmbedded';
import PortalBackground from '../components/PortalBackground';
import { PYRAMID_LAYERS, TECH_TIERS, fetchTechTierMeta } from '../utils/techStackTiers';

const AllSoftware = ({ embedded: embeddedProp }) => {
  const { darkMode } = useTheme();
  const embedded = useTechnologyEmbedded(embeddedProp);
  const [searchParams] = useSearchParams();
  // Portal search deep-links land here as ?search=<tool name>.
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [tierMeta, setTierMeta] = useState(() => TECH_TIERS.map((t) => ({ ...t, title: t.defaultTitle })));
  const [softwareList, setSoftwareList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState(new Set());
  const navigate = useNavigate();

  // Keep the search box in sync when a deep link changes only the query string
  // (no remount happens if the user is already on this page).
  useEffect(() => {
    const q = searchParams.get('search');
    if (q) setSearchTerm(q);
  }, [searchParams]);

  // Fetch data from Firebase
  useEffect(() => {
    const fetchSoftwareData = async () => {
      try {
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        const allSoftware = [];
        setTierMeta(await fetchTechTierMeta());

        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
          
          for (const typeDoc of typesSnapshot.docs) {
            const typeData = typeDoc.data();
            const softwareSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares'));
            
            softwareSnapshot.forEach(softwareDoc => {
              const data = softwareDoc.data();
              if (data && data.name) {
                // Get the first pricing tier if available
                const firstPricingTier = data.pricing && data.pricing.length > 0 ? data.pricing[0] : null;
                const monthlyCost = firstPricingTier ? Number(firstPricingTier.monthlyCost) || 0 : 0;

                // Handle useCases safely
                let features = [];
                if (typeof data.useCases === 'string') {
                  features = data.useCases.split(',').map(item => item.trim());
                } else if (Array.isArray(data.useCases)) {
                  features = data.useCases;
                }

                allSoftware.push({
                  id: softwareDoc.id,
                  typeId: typeDoc.id,
                  name: data.name,
                  category: typeData.name,
                  tier: collectionName,
                  description: data.description || '',
                  monthlyCost: monthlyCost,
                  website: data.website || '',
                  features: features,
                  image: data.image || '',
                  suggested: data.suggested || false,
                  pricing: data.pricing || [],
                  useCases: data.useCases || '',
                  open: data.open || false
                });
              }
            });
          }
        }

        console.log('Fetched software:', allSoftware);
        setSoftwareList(allSoftware);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching software data:', error);
        setIsLoading(false);
      }
    };

    fetchSoftwareData();
  }, []);

  // Define categories and tiers after softwareList is initialized
  const categories = ['all', ...new Set(softwareList
    .filter(item => item && item.category) // Filter out null/undefined items
    .map(item => item.category)
  )];
  // Tier doc ids in pyramid order; labels and badge colours follow the template
  const tiers = ['all', ...tierMeta.map((t) => t.docId)];
  const tierTitle = (docId) => tierMeta.find((t) => t.docId === docId)?.title || docId;
  const tierColor = (docId) => PYRAMID_LAYERS[Math.max(0, tierMeta.findIndex((t) => t.docId === docId))].color;

  // Define filteredSoftware after all dependencies are initialized
  const filteredSoftware = softwareList.filter(item => {
    if (!item) return false;
    
    const matchesSearch = searchTerm === '' || 
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesTier = selectedTier === 'all' || item.tier === selectedTier;
    
    return matchesSearch && matchesCategory && matchesTier;
  });

  // Debug logs for filtering
  useEffect(() => {
    if (softwareList.length > 0) {
      console.log('Current filters:', {
        searchTerm,
        selectedCategory,
        selectedTier,
        totalSoftware: softwareList.length,
        filteredCount: filteredSoftware.length,
        categories: categories,
        softwareList: softwareList
      });
    }
  }, [searchTerm, selectedCategory, selectedTier, softwareList, filteredSoftware, categories]);

  const handleSoftwareClick = (software) => {
    navigate(`/portal/technology/software/${software.tier}/${software.typeId}/${software.id}`);
  };

  return (
    <div className={embedded ? 'w-full' : 'w-full min-h-screen p-6 relative'}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-7xl mx-auto">
        {!embedded && (
          <div className="flex justify-between items-center mb-6">
            <BackButton to="/portal/technology/tech-stack" />
            <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center`}>All Software</h1>
            <div className="w-[60px]" />
          </div>
        )}

        {/* Search and Filter Section */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-6">
              <label htmlFor="search" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Search Software
              </label>
              <input
                type="text"
                id="search"
                className={`block w-full border rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500 ${
                  darkMode 
                    ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' 
                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor="tier" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Tier
              </label>
              <select
                id="tier"
                className={`block w-full border rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500 ${
                  darkMode 
                    ? 'border-gray-600 bg-gray-700 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
              >
                {tiers.map(tier => (
                  <option key={tier} value={tier}>
                    {tier === 'all' ? 'All' : tierTitle(tier)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label htmlFor="category" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Category
              </label>
              <select
                id="category"
                className={`block w-full border rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500 ${
                  darkMode 
                    ? 'border-gray-600 bg-gray-700 text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <BBALoader size={32} className="inline-block" />
            <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading software data...</p>
          </div>
        )}

        {/* Software Grid */}
        {!isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredSoftware.map(software => (
              <button
                key={software.id}
                onClick={() => handleSoftwareClick(software)}
                className="w-full text-left bg-transparent border-0 p-0 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-lg"
              >
                <motion.div
                  className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="p-4">
                    <div className="flex flex-col items-center text-center">
                      {/* Logo */}
                      <div className="w-20 h-20 mb-3">
                        {software.image ? (
                          (() => {
                            const imageKey = `all-software-${software.id}`;
                            const hasImageError = imageErrors.has(imageKey);
                            
                            if (hasImageError) {
                              return (
                                <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                  <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} text-2xl`}>?</span>
                                </div>
                              );
                            }
                            
                            return (
                              <img 
                                src={software.image} 
                                alt={software.name}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '';
                                  setImageErrors(prev => new Set([...prev, imageKey]));
                                }}
                              />
                            );
                          })()
                        ) : (
                          <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                            <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} text-2xl`}>?</span>
                          </div>
                        )}
                      </div>

                      {/* Title */}
                      <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                        {software.name}
                        {software.suggested && (
                          <span className="ml-1 text-red-600">★</span>
                        )}
                      </h2>

                      {/* Badges */}
                      <div className="flex flex-wrap justify-center gap-1">
                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                          {software.category}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded text-white ${tierColor(software.tier)}`}>
                          {tierTitle(software.tier)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </button>
            ))}
          </div>
        )}

        {!isLoading && filteredSoftware.length === 0 && (
          <div className="text-center py-12">
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-lg`}>No software found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllSoftware; 